import {useMutation, useQueryClient} from '@tanstack/react-query';
import {useNavigate} from 'react-router-dom';
import {
  updatePayment,
  UpdatePaymentPayload,
  UpdatePaymentResponse,
} from '$api/frontdesk';
import toast from '~/utils/toast';
import {ToastType} from '~/components/Toast/constant';
import {PAYMENT_DETAILS_STRINGS} from '../../Finance/PaymentDetails/constant';
import {RoutePath} from '$constants/routes';

interface UseUpdatePaymentHookOptions {
  onPaymentSuccess?: (paymentId: string) => void;
  onPaymentLinkSuccess?: () => void;
  customSuccessMessage?: string;
}

export const useUpdatePaymentHook = (options?: UseUpdatePaymentHookOptions) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation<UpdatePaymentResponse, Error, UpdatePaymentPayload>({
    mutationFn: updatePayment,
    onSuccess: (_response, variables) => {
      const paymentId = variables.payment_id;

      queryClient.invalidateQueries({queryKey: ['payment-details', paymentId]});
      queryClient.invalidateQueries({queryKey: ['payment-details']});

      const isPaymentLink = variables.send_via_email || variables.send_via_sms;

      if (isPaymentLink) {
        queryClient.refetchQueries({queryKey: ['payment-details', paymentId]});
        toast(
          ToastType.Success,
          'Success',
          options?.customSuccessMessage ||
            PAYMENT_DETAILS_STRINGS.PAYMENT_LINK_SENT_SUCCESS
        );
        options?.onPaymentLinkSuccess?.();
      } else {
        if (options?.onPaymentSuccess) {
          options.onPaymentSuccess(String(paymentId));
        } else {
          const successRoute = `${RoutePath.staff.index}/${RoutePath.staff.finance.index}/payments/${paymentId}/payment-success`;
          navigate(successRoute, {
            state: {paymentId},
          });
        }
      }
    },
    onError: error => {
      toast(ToastType.Error, 'Error', error.message || 'Payment update failed');
    },
  });
};
